import { HttpClient } from '@angular/common/http';
import { IParkingLocationModel } from '@aggregator/core';

import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { UIBaseModal, UIButton, UIFormModule, UIModal } from '@shared/ui';
import * as L from 'leaflet';
import { debounceTime, Subject } from 'rxjs';
import { ParkingService } from '../../services';

interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
}

@Component({
  selector: 'aggregator-parking-map-button-modal',
  templateUrl: './parking-map-modal.component.html',
  styleUrls: ['./parking-map-modal.component.scss'],
  standalone: true,
  imports: [UIModal, UIFormModule, UIButton, FormsModule],
  providers: [ParkingService],
})
export class ParkingMapModal
  extends UIBaseModal<IParkingLocationModel | null>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('mapContainer', { static: false }) mapContainer?: ElementRef;

  map?: L.Map;
  selectedMarker?: L.Marker;
  searchQuery = '';
  searchResults: NominatimResult[] = [];
  isSearching = false;
  selectedCoordinates?: { lat?: number; lon?: number };

  private searchSubject = new Subject<string>();
  private readonly ASTANA_CENTER: [number, number] = [51.1694, 71.4491];
  private readonly DEFAULT_ZOOM = 13;

  get title(): string {
    return this.data ? 'Редактировать координаты' : 'Указать на карте';
  }

  get canConfirm(): boolean {
    return !!this.selectedMarker;
  }

  constructor(private http: HttpClient, private destroyRef: DestroyRef) {
    super();
    this.setupSearchDebounce();
  }

  ngAfterViewInit(): void {
    // Defer to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.initializeMap();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(query => {
        this.performSearch(query);
      });
  }

  private initializeMap(): void {
    if (!this.mapContainer?.nativeElement) {
      return;
    }

    const center: [number, number] =
      this.data && this.data.y && this.data.x
        ? [this.data.x, this.data.y] // data.x = lat, data.y = lon
        : this.ASTANA_CENTER;

    this.map = L.map(this.mapContainer.nativeElement, {
      center,
      zoom: this.data ? 15 : this.DEFAULT_ZOOM,
      attributionControl: false,
    });

    // Add tile layer (2GIS)
    L.tileLayer('https://tile2.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1.1&r=g&s=1024&ts=online', {
      maxZoom: 18,
      attribution: '2GIS',
    }).addTo(this.map);

    // Add click handler
    this.map.on('click', (e: L.LeafletMouseEvent) => {
      this.onMapClick(e);
    });

    // If in edit mode, add initial marker
    if (this.data && this.data.y && this.data.x) {
      this.addMarker(this.data.x, this.data.y);
    }
  }

  private onMapClick(event: L.LeafletMouseEvent): void {
    const { lat, lng } = event.latlng;
    this.addMarker(lat, lng);
  }

  private addMarker(lat: number, lon: number): void {
    // Remove previous marker if exists
    if (this.selectedMarker) {
      this.map?.removeLayer(this.selectedMarker);
    }

    // Create custom icon with visible pin
    const icon = L.divIcon({
      className: 'custom-marker-icon',
      html: `
        <div class="marker-pin">
          <svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 0C8.373 0 3 5.373 3 12c0 9 12 28 12 28s12-19 12-28c0-6.627-5.373-12-12-12z" 
                  fill="#ff5733" stroke="#fff" stroke-width="2"/>
            <circle cx="15" cy="12" r="4" fill="#fff"/>
          </svg>
        </div>
      `,
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -40],
    });

    // Add new marker
    if (this.map) {
      this.selectedMarker = L.marker([lat, lon], { icon }).addTo(this.map);
      this.selectedCoordinates = { lat, lon };
    }
  }

  onSearchInput(): void {
    if (this.searchQuery.trim().length < 3) {
      this.searchResults = [];
      return;
    }
    this.searchSubject.next(this.searchQuery);
  }

  private performSearch(query: string): void {
    if (!query || query.trim().length < 3) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;

    this.http
      .get<NominatimResult[]>('https://nominatim.openstreetmap.org/search', {
        params: {
          format: 'json',
          q: query,
          limit: '5',
          countrycodes: 'kz',
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: results => {
          this.searchResults = results;
          this.isSearching = false;
        },
        error: () => {
          this.isSearching = false;
          this.searchResults = [];
        },
      });
  }

  onSelectSearchResult(result: NominatimResult): void {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    // Move map to location
    this.map?.setView([lat, lon], 16);

    // Add marker
    this.addMarker(lat, lon);

    // Clear search
    this.searchQuery = '';
    this.searchResults = [];
  }

  onConfirm(): void {
    if (!this.selectedCoordinates) {
      return;
    }

    const result: IParkingLocationModel = {
      x: this.selectedCoordinates.lat, // longitude
      y: this.selectedCoordinates.lon, // latitude
    };

    this.onAfterClose(result);
  }

  onCancel(): void {
    this.onAfterClose(null);
  }
}
